import { applyDelta, type DeltaMsg } from '../net/delta.js';
import { extrapolateState } from '../net/extrapolate.js';
import { PROTOCOL_VERSION, decode, encode, type ClientMsg, type ServerMsg } from '../net/protocol.js';
import { Room } from '../net/room.js';
import { KNIFE_FLY_RADIUS, KNIFE_RECATCH_DELAY, PLAYER_RADIUS, SNAPSHOT_EVERY_TICKS, TICK_RATE } from '../sim/constants.js';
import { lerp, lerpAngle } from '../sim/geom.js';
import { getArena } from '../sim/arena.js';
import { createThrow, flyKnife, predictLocalPlayer, separateBodies, touchesGroundKnife } from '../sim/sim.js';
import {
  EMPTY_INPUT,
  copyInput,
  type FlyingKnife,
  type GameEvent,
  type GameState,
  type KnifeState,
  type PlayerInput,
  type PlayerState,
} from '../sim/types.js';

export interface Frame {
  state: GameState | null;
  events: GameEvent[];
}

export interface NetStats {
  rttMs: number;
  /** Arrival jitter of snapshots over the last ~2 s, ms. */
  jitterMs: number;
  /** Inputs the server had buffered for us at the last snapshot, and the target the clock control aims for. */
  buffered: number;
  targetBuffer: number;
  /** Current tick clock rate (1 = nominal); above 1 means we are catching up. */
  clockRate: number;
  /** Ticks the remote view is rendered behind the estimated server tick. */
  interpDelayTicks: number;
  /** Deltas that referenced a baseline we no longer had (should stay 0). */
  deltaMisses: number;
  /** Ticks the remote view is currently extrapolated ahead of the newest snapshot. */
  extrapolated: number;
  /** Inputs sent but not yet acknowledged by a snapshot (per local player, max). */
  unacked: number;
  /** Distance of the last reconciliation correction, px. */
  lastCorrection: number;
}

/**
 * Where the authoritative simulation lives. The scene only talks to this
 * interface, so couch play (LocalHost) and online play (NetHost) render and
 * control identically. A single client may own several players (one per
 * local control slot) on either host.
 */
export interface Host {
  readonly kind: 'local' | 'net';
  readonly status: string;
  /** Called when a slot's player has been created and has an id. */
  onJoined: ((slot: string, id: number) => void) | null;
  join(slot: string, name: string, color: number): void;
  leave(id: number): void;
  setInput(id: number, input: PlayerInput): void;
  /** Advance / flush; call once per rendered frame. */
  update(nowMs: number): void;
  /** State to render for this frame plus events since the previous frame. */
  frame(nowMs: number): Frame;
  stats(): NetStats | null;
  destroy(): void;
}

const TICK_MS = 1000 / TICK_RATE;

// ---------------------------------------------------------------- local

export class LocalHost implements Host {
  readonly kind = 'local' as const;
  readonly status = 'offline couch mode';
  onJoined: Host['onJoined'] = null;
  private readonly room: Room;
  private acc = 0;
  private last = -1;

  constructor(seed: number) {
    this.room = new Room(seed);
  }

  join(slot: string, name: string, color: number): void {
    const id = this.room.addPlayer(name, color);
    this.onJoined?.(slot, id);
  }

  leave(id: number): void {
    this.room.removePlayer(id);
  }

  setInput(id: number, input: PlayerInput): void {
    this.room.setInput(id, input);
  }

  update(nowMs: number): void {
    if (this.last < 0) this.last = nowMs;
    this.acc += Math.min(250, nowMs - this.last);
    this.last = nowMs;
    while (this.acc >= TICK_MS) {
      this.acc -= TICK_MS;
      this.room.tick();
    }
  }

  frame(): Frame {
    return { state: this.room.state, events: this.room.flushEvents() };
  }

  stats(): null {
    return null;
  }

  destroy(): void {}
}

// ------------------------------------------------------------------ net

interface Snapshot {
  state: GameState;
  tick: number;
  /** Local receipt time in ms. */
  at: number;
}

interface LocalPlayer {
  /** Latest sampled input from the controls. */
  cur: PlayerInput;
  /** Inputs sent but not yet acknowledged, oldest first. */
  pending: Array<{ seq: number; input: PlayerInput }>;
  /** Input of the last acknowledged seq, needed for edge detection when replaying. */
  lastAcked: PlayerInput;
  /** Predicted state: last snapshot + replayed pending inputs + inputs since. */
  predicted: PlayerState | null;
  /** Visual error left over from the last correction, decays towards zero. */
  errX: number;
  errY: number;
  errHeading: number;
}

/**
 * The client's belief about the knife while the server has not confirmed a
 * local throw or pickup yet. Deterministic flight means a predicted throw
 * lands exactly where the server's will.
 */
interface KnifePrediction {
  knife: KnifeState;
  kind: 'throw' | 'pickup';
  /** Player the prediction belongs to (thrower or picker). */
  by: number;
  sinceTick: number;
}

/** Give up on a knife prediction the server never confirmed after this many client ticks. */
const KNIFE_PREDICTION_TIMEOUT_TICKS = 90;

const SESSION_KEY = 'puukkohippa-session';

/**
 * Random id that also works on plain-http origins, where browsers do not
 * expose `crypto.randomUUID` (it is secure-context only).
 */
function randomId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Stable per-tab id so a reconnect resumes the same players. */
function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return randomId();
  }
}

/**
 * Remote view timing. The render clock sits behind the estimated server
 * tick by one snapshot interval plus measured jitter plus a margin, so there
 * is almost always a newer snapshot to interpolate towards; it adapts to
 * the connection instead of assuming a fixed 117 ms.
 */
const MIN_INTERP_DELAY_TICKS = 5;
const MAX_INTERP_DELAY_TICKS = 18; // 300 ms
const SNAPSHOT_INTERVAL_MS = SNAPSHOT_EVERY_TICKS * (1000 / TICK_RATE);
/** Snapshot arrival samples kept for clock and jitter estimation (~2 s at 20 Hz). */
const TIMING_WINDOW = 40;
/** Input buffer target on the server, in ticks, bounds. */
const MIN_TARGET_BUFFER = 2;
const MAX_TARGET_BUFFER = 8; // 133 ms of input latency at most
/** Tick clock rate bounds for the buffer control loop (time dilation). */
const MIN_CLOCK_RATE = 0.9;
const MAX_CLOCK_RATE = 1.1;
/** How fast reconciliation errors are smoothed out (fraction per second). */
const ERROR_DECAY_PER_S = 18;
/** Corrections larger than this snap instead of smoothing. */
const SNAP_DISTANCE = 120;

/** Artificial latency for testing: every message is delayed by `delayMs` ± `jitterMs`, order preserved (like TCP). */
export interface NetSim {
  delayMs: number;
  jitterMs: number;
  /** Benchmark knob: disable the input-buffer clock control (fixed 60 Hz tick clock). */
  noClockControl?: boolean;
  /** Every `stallEveryMs`, hold all traffic for `stallMs` (a TCP retransmit stall). */
  stallEveryMs?: number;
  stallMs?: number;
}

/** FIFO delayed delivery: one timer chain, so entries never overtake each other. */
class DelayQueue {
  private readonly items: Array<{ at: number; fn: () => void }> = [];
  private lastAt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  push(at: number, fn: () => void): void {
    const when = Math.max(at, this.lastAt);
    this.lastAt = when;
    this.items.push({ at: when, fn });
    if (!this.timer) this.pump();
  }

  private pump(): void {
    this.timer = null;
    const now = performance.now();
    while (this.items.length && this.items[0]!.at <= now) this.items.shift()!.fn();
    if (this.items.length) this.timer = setTimeout(() => this.pump(), Math.max(1, this.items[0]!.at - now));
  }
}

export class NetHost implements Host {
  readonly kind = 'net' as const;
  status = 'connecting';
  onJoined: Host['onJoined'] = null;
  private readonly simOut = new DelayQueue();
  private readonly simIn = new DelayQueue();
  private simNextStall = 0;
  private simStallUntil = 0;

  private ws: WebSocket | null = null;
  private readonly snapshots: Snapshot[] = [];
  private pendingEvents: GameEvent[] = [];
  private readonly slots = new Map<string, { name: string; color: number; id: number | null }>();
  private readonly local = new Map<number, LocalPlayer>();
  private readonly session = sessionId();
  private knifePred: KnifePrediction | null = null;
  /**
   * True while the knife's last owner or thrower was one of our players. Such
   * a knife is drawn on the present timeline (newest snapshot, extrapolated)
   * so it never lags behind the predicted player; a knife in remote hands is
   * drawn on the interpolated timeline that matches the remote players.
   */
  private knifeLocal = false;
  /** Players as drawn last frame; the predicted knife checks hits against them. */
  private lastDrawn: PlayerState[] = [];
  private clientTick = 0;
  private acc = 0;
  private last = -1;
  private lastFrameAt = -1;
  /**
   * Snapshot arrival timing: samples of `localTime - serverTick * TICK_MS`.
   * The minimum over the window is the offset of the fastest packets (the
   * true clock relation); the spread above it is jitter.
   */
  private readonly offsetSamples: number[] = [];
  /** Local time at which the remote view's tick 0 "happens", smoothed by rate not by jumps. */
  private renderOffset: number | null = null;
  private renderOffsetAt = 0;
  /** Server input buffer depth for our players, smoothed. */
  private bufferDepth: number | null = null;
  /** Tick clock rate: >1 runs ticks faster to refill the server buffer, <1 to drain it. */
  private clockRate = 1;
  /** Snapshots handled before this time arrived late because *we* stalled, not the network. */
  private stallUntil = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lastPingAt = 0;
  private closed = false;
  private readonly netStats: NetStats = {
    rttMs: 0,
    jitterMs: 0,
    buffered: 0,
    targetBuffer: 2,
    clockRate: 1,
    interpDelayTicks: MIN_INTERP_DELAY_TICKS,
    deltaMisses: 0,
    extrapolated: 0,
    unacked: 0,
    lastCorrection: 0,
  };

  constructor(
    private readonly url: string,
    private readonly room: string,
    private readonly netsim: NetSim | null = null,
  ) {
    this.connect();
  }

  /** Delay a callback per the network simulator, never reordering (TCP semantics). */
  private simulate(direction: 'in' | 'out', fn: () => void): void {
    const sim = this.netsim;
    if (!sim) return fn();
    const now = performance.now();
    let at = now + sim.delayMs + (Math.random() * 2 - 1) * sim.jitterMs;
    if (sim.stallEveryMs && sim.stallMs) {
      if (this.simNextStall === 0) this.simNextStall = now + sim.stallEveryMs;
      if (now >= this.simNextStall) {
        this.simStallUntil = now + sim.stallMs;
        this.simNextStall = now + sim.stallEveryMs;
      }
      at = Math.max(at, this.simStallUntil + sim.delayMs);
    }
    (direction === 'in' ? this.simIn : this.simOut).push(at, fn);
  }

  private connect(): void {
    if (this.closed) return;
    this.status = `connecting to ${this.room}`;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.send({ t: 'hello', room: this.room, v: PROTOCOL_VERSION, session: this.session });
      // Re-register every slot we already had (reconnect case).
      for (const [slot, s] of this.slots) {
        s.id = null;
        this.send({ t: 'join', slot, name: s.name, color: s.color });
      }
      this.local.clear();
      this.offsetSamples.length = 0;
      this.renderOffset = null;
      this.bufferDepth = null;
      this.clockRate = 1;
    };
    ws.onmessage = (ev) => {
      const raw = String(ev.data);
      this.simulate('in', () => {
        const msg = decode<ServerMsg>(raw);
        if (msg) this.handle(msg);
      });
    };
    ws.onclose = (ev) => {
      this.snapshots.length = 0;
      this.knifePred = null;
      if (ev.code === 4000) {
        // Another tab with the same session took over; do not fight it.
        this.status = 'this session is open in another tab';
        return;
      }
      this.status = 'disconnected, retrying…';
      if (!this.closed) this.reconnectTimer = setTimeout(() => this.connect(), 1500);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private send(msg: ClientMsg): void {
    const raw = encode(msg);
    this.simulate('out', () => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(raw);
    });
  }

  private handle(msg: ServerMsg): void {
    switch (msg.t) {
      case 'welcome':
        this.status = `online · room "${msg.room}"`;
        break;
      case 'joined': {
        const s = this.slots.get(msg.slot);
        if (s) s.id = msg.id;
        if (this.local.has(msg.id)) break; // reconnect re-announcing an existing player
        this.local.set(msg.id, {
          cur: copyInput(EMPTY_INPUT),
          pending: [],
          lastAcked: copyInput(EMPTY_INPUT),
          predicted: null,
          errX: 0,
          errY: 0,
          errHeading: 0,
        });
        this.onJoined?.(msg.slot, msg.id);
        break;
      }
      case 'snapshot':
        this.onSnapshot(msg.state, msg.acks, msg.bufs);
        this.send({ t: 'ack', tick: msg.state.tick });
        break;
      case 'delta':
        this.onDelta(msg);
        break;
      case 'pong':
        this.netStats.rttMs = performance.now() - msg.sent;
        break;
      case 'error':
        this.status = `server: ${msg.message}`;
        break;
    }
  }

  private onDelta(d: DeltaMsg): void {
    const base = this.snapshots.find((s) => s.tick === d.base);
    if (!base) {
      // We no longer have the baseline; keep acking what we have so the
      // server falls back to a full snapshot.
      const newest = this.snapshots[this.snapshots.length - 1];
      if (newest) this.send({ t: 'ack', tick: newest.tick });
      this.netStats.deltaMisses++;
      return;
    }
    const state = applyDelta(base.state, d);
    this.onSnapshot(state, d.acks, d.bufs);
    this.send({ t: 'ack', tick: state.tick });
  }

  private onSnapshot(state: GameState, acks: Record<string, number>, bufs: Record<string, number>): void {
    const at = performance.now();
    // The wire strips prevInput; give every player a valid one.
    for (const p of state.players) p.prevInput = copyInput(EMPTY_INPUT);
    this.pendingEvents.push(...state.events);
    state.events = [];
    this.snapshots.push({ state, tick: state.tick, at });
    while (this.snapshots.length > 32) this.snapshots.shift();

    // If our frame loop has not run for a while, this snapshot was delayed by
    // us (GC, hidden tab), not by the network: keep it out of the jitter estimate.
    const stalled = this.last >= 0 && at - this.last > 60;
    if (!stalled && at >= this.stallUntil) {
      this.offsetSamples.push(at - state.tick * TICK_MS);
      while (this.offsetSamples.length > TIMING_WINDOW) this.offsetSamples.shift();
    }
    this.controlClock(bufs);

    const k = state.knife;
    if (k.mode === 'held') this.knifeLocal = this.local.has(k.holder);
    else if (k.mode === 'flying') this.knifeLocal = this.local.has(k.thrower);
    // On the ground it keeps the timeline of whoever last had it.

    this.reconcile(state, acks);
    this.reconcileKnife(state);
  }

  /** Drop the knife prediction once the server has caught up with it (or clearly disagrees). */
  private reconcileKnife(state: GameState): void {
    const kp = this.knifePred;
    if (!kp) return;
    const k = state.knife;
    if (kp.kind === 'throw') {
      // The predicted flight is the server's flight, just earlier: keep it
      // while the server still shows the knife in the thrower's hand (throw
      // not applied yet) or in the air. Switch to the truth once the server
      // has landed it or someone holds it, which is where the prediction
      // already is (deterministic) or where we mispredicted.
      const notAppliedYet = k.mode === 'held' && k.holder === kp.by;
      const inFlight = k.mode === 'flying' && k.thrower === kp.by;
      if (notAppliedYet) {
        if (state.tick - kp.sinceTick > KNIFE_PREDICTION_TIMEOUT_TICKS) this.knifePred = null; // never happened
      } else if (!inFlight) {
        this.knifePred = null;
      }
    } else if (k.mode === 'held' || k.mode === 'flying') {
      // Pickup confirmed, or the knife went somewhere else entirely.
      this.knifePred = null;
    }
  }

  /** Rebuild each local player's prediction from the authoritative snapshot. */
  private reconcile(state: GameState, acks: Record<string, number>): void {
    let maxUnacked = 0;
    const before = new Map<number, PlayerState>();
    const replay = new Map<number, LocalPlayer>();
    for (const [id, lp] of this.local) {
      const authoritative = state.players.find((p) => p.id === id);
      if (!authoritative) {
        lp.predicted = null;
        continue;
      }
      const ack = acks[String(id)] ?? 0;
      while (lp.pending.length && lp.pending[0]!.seq <= ack) {
        lp.lastAcked = lp.pending.shift()!.input;
      }
      maxUnacked = Math.max(maxUnacked, lp.pending.length);
      if (lp.predicted) before.set(id, lp.predicted);
      lp.predicted = { ...authoritative, prevInput: copyInput(lp.lastAcked) };
      replay.set(id, lp);
    }
    // Replay seq by seq across all local players, separating them after each
    // tick exactly like the server does.
    let lo = Infinity;
    let hi = -Infinity;
    for (const lp of replay.values()) {
      if (lp.pending.length) {
        lo = Math.min(lo, lp.pending[0]!.seq);
        hi = Math.max(hi, lp.pending[lp.pending.length - 1]!.seq);
      }
    }
    for (let seq = lo; seq <= hi; seq++) {
      for (const lp of replay.values()) {
        const e = lp.pending.find((q) => q.seq === seq);
        if (e && lp.predicted) predictLocalPlayer(state, lp.predicted, e.input);
      }
      this.separateLocals(state);
    }
    for (const [id, lp] of replay) {
      const prev = before.get(id);
      const next = lp.predicted!;
      if (!prev) continue;
      // Carry the visual difference so the correction is smoothed, not popped.
      const dx = prev.x + lp.errX - next.x;
      const dy = prev.y + lp.errY - next.y;
      const dist = Math.hypot(dx, dy);
      this.netStats.lastCorrection = Math.hypot(prev.x - next.x, prev.y - next.y);
      if (dist > SNAP_DISTANCE || prev.role !== next.role) {
        lp.errX = lp.errY = lp.errHeading = 0;
      } else {
        lp.errX = dx;
        lp.errY = dy;
        lp.errHeading = wrap(prev.heading + lp.errHeading - next.heading);
      }
    }
    this.netStats.unacked = maxUnacked;
  }

  join(slot: string, name: string, color: number): void {
    if (this.slots.has(slot)) return;
    this.slots.set(slot, { name, color, id: null });
    this.send({ t: 'join', slot, name, color });
  }

  leave(id: number): void {
    for (const [slot, s] of this.slots) if (s.id === id) this.slots.delete(slot);
    this.local.delete(id);
    this.send({ t: 'leave', id });
  }

  setInput(id: number, input: PlayerInput): void {
    const lp = this.local.get(id);
    if (lp) lp.cur = copyInput(input);
  }

  update(nowMs: number): void {
    if (this.last < 0) this.last = nowMs;
    // A stalled frame (tab hidden, GC) must not burst a pile of ticks at the
    // server; anything beyond 100 ms is simply lost time. Messages that piled
    // up during the stall are not evidence of network jitter either.
    const dt = nowMs - this.last;
    if (dt > 60) this.stallUntil = performance.now() + 50;
    this.acc += Math.min(100, dt);
    this.last = nowMs;
    const latest = this.snapshots[this.snapshots.length - 1]?.state ?? null;
    const tickMs = TICK_MS / this.clockRate;
    while (this.acc >= tickMs) {
      this.acc -= tickMs;
      this.clientTick++;
      if (this.local.size === 0) continue;
      const batch: Array<{ id: number; input: PlayerInput }> = [];
      for (const [id, lp] of this.local) {
        const input = copyInput(lp.cur);
        lp.pending.push({ seq: this.clientTick, input });
        if (lp.pending.length > 600) lp.pending.shift(); // 10 s without acks: stop growing
        batch.push({ id, input });
        if (lp.predicted && latest) {
          const chargeBefore = lp.predicted.charge;
          const intent = predictLocalPlayer(latest, lp.predicted, input);
          this.predictKnife(latest, lp.predicted, intent.kind === 'none' ? null : { intent, charge: chargeBefore });
        }
      }
      if (latest) this.separateLocals(latest);
      this.send({ t: 'input', seq: this.clientTick, view: Math.floor(this.renderTick()), inputs: batch });
      this.stepKnifePrediction(latest);
    }
    if (nowMs - this.lastPingAt > 2000) {
      this.lastPingAt = nowMs;
      this.send({ t: 'ping', sent: performance.now() });
    }
  }

  /**
   * Body separation between our own players is deterministic (both are
   * predicted exactly), so predict it too; otherwise two players on one
   * device bumping into each other get corrected every snapshot.
   */
  private separateLocals(latest: GameState): void {
    const ps: PlayerState[] = [];
    for (const lp of this.local.values()) if (lp.predicted) ps.push(lp.predicted);
    if (ps.length < 2) return;
    const arena = getArena(latest.seed);
    for (let i = 0; i < ps.length; i++) for (let j = i + 1; j < ps.length; j++) separateBodies(arena, ps[i]!, ps[j]!);
  }

  /** Start a knife prediction for a local player's throw or pickup. */
  private predictKnife(
    latest: GameState,
    p: PlayerState,
    release: { intent: ReturnType<typeof predictLocalPlayer>; charge: number } | null,
  ): void {
    if (this.knifePred) return;
    const serverKnife = latest.knife;
    const arena = getArena(latest.seed);
    if (release && serverKnife.mode === 'held' && serverKnife.holder === p.id) {
      const it = release.intent;
      let angle = p.heading;
      let target = -1;
      if (it.kind === 'pass') {
        // A local target's predicted position is what the server will see when it applies the throw.
        const t = this.local.get(it.target.id)?.predicted ?? it.target;
        angle = Math.atan2(t.y - p.y, t.x - p.x);
        target = it.target.id;
      }
      const knife = createThrow(arena, p, angle, release.charge, target);
      if (knife) this.knifePred = { knife, kind: 'throw', by: p.id, sinceTick: latest.tick };
      return;
    }
    if (p.role === 'puukottaja' && latest.phase === 'playing' && touchesGroundKnife(this.presentKnife(latest), p)) {
      this.knifePred = { knife: { mode: 'held', holder: p.id }, kind: 'pickup', by: p.id, sinceTick: latest.tick };
    }
  }

  /**
   * Fly the predicted knife one tick and let it hit players as drawn: local
   * players at their predicted positions (exactly what the server will see
   * for them) and remote players at their interpolated positions (which is
   * what the server's lag compensation rewinds them to for our throws).
   */
  private stepKnifePrediction(latest: GameState | null): void {
    const kp = this.knifePred;
    if (!kp || !latest || kp.knife.mode !== 'flying') return;
    const drawn = this.lastDrawn;
    const result = flyKnife(getArena(latest.seed), { ...kp.knife }, (fk) => {
      for (const p of drawn) {
        if (!p.connected) continue;
        if (p.id === fk.thrower && fk.airTime < KNIFE_RECATCH_DELAY) continue;
        if (Math.hypot(p.x - fk.x, p.y - fk.y) >= PLAYER_RADIUS + KNIFE_FLY_RADIUS) continue;
        kp.knife = { mode: 'held', holder: p.id };
        return true;
      }
      return false;
    });
    if (kp.knife.mode === 'flying') kp.knife = result;
  }

  /** The newest server knife, advanced to "now" while flying so it keeps up with the predicted thrower. */
  private presentKnife(latest: GameState): KnifeState {
    const k = latest.knife;
    if (k.mode !== 'flying') return k;
    const newest = this.snapshots[this.snapshots.length - 1];
    if (!newest) return k;
    const ahead = Math.max(0, Math.min(10, Math.round((performance.now() - newest.at) / TICK_MS)));
    const arena = getArena(latest.seed);
    let cur: KnifeState = { ...k };
    for (let i = 0; i < ahead && cur.mode === 'flying'; i++) cur = flyKnife(arena, cur as FlyingKnife, null);
    return cur;
  }

  frame(nowMs: number): Frame {
    const events = this.pendingEvents;
    this.pendingEvents = [];
    const dt = this.lastFrameAt < 0 ? 0 : Math.min(0.1, (nowMs - this.lastFrameAt) / 1000);
    this.lastFrameAt = nowMs;

    const base = this.interpolated();
    if (!base) return { state: null, events };

    // Local players come from prediction, everyone else from interpolation.
    const decay = Math.exp(-ERROR_DECAY_PER_S * dt);
    const players = base.players.map((p) => {
      const lp = this.local.get(p.id);
      if (!lp?.predicted) return p;
      lp.errX *= decay;
      lp.errY *= decay;
      lp.errHeading *= decay;
      return { ...lp.predicted, x: lp.predicted.x + lp.errX, y: lp.predicted.y + lp.errY, heading: lp.predicted.heading + lp.errHeading };
    });
    const latest = this.snapshots[this.snapshots.length - 1]?.state ?? base;
    const knife = this.knifePred?.knife ?? (this.knifeLocal ? this.presentKnife(latest) : base.knife);
    this.lastDrawn = players;
    return { state: { ...base, players, knife }, events };
  }

  /** Fastest-packet clock offset and jitter (90th percentile above the minimum) over the window. */
  private timing(): { offset: number; jitterMs: number } | null {
    const n = this.offsetSamples.length;
    if (n === 0) return null;
    const sorted = this.offsetSamples.slice().sort((a, b) => a - b);
    const offset = sorted[0]!;
    const p90 = sorted[Math.min(n - 1, Math.floor(n * 0.9))]!;
    return { offset, jitterMs: p90 - offset };
  }

  /**
   * Time dilation (the Overwatch approach): the server reports how many of
   * our inputs it still has queued; we run our tick clock a little faster
   * when the buffer is below target and slower when above, so it hovers at
   * a depth that covers the measured jitter and never runs dry (which would
   * make the server repeat an input and cause a correction).
   */
  private controlClock(bufs: Record<string, number>): void {
    let depth: number | null = null;
    for (const id of this.local.keys()) {
      const b = bufs[String(id)];
      if (b !== undefined) depth = depth === null ? b : Math.min(depth, b);
    }
    const t = this.timing();
    const jitterTicks = t ? Math.ceil(t.jitterMs / TICK_MS) : 0;
    const target = Math.max(MIN_TARGET_BUFFER, Math.min(MAX_TARGET_BUFFER, jitterTicks + 1));
    this.netStats.targetBuffer = target;
    this.netStats.jitterMs = t?.jitterMs ?? 0;
    if (depth === null) return;
    this.bufferDepth = this.bufferDepth === null ? depth : this.bufferDepth + (depth - this.bufferDepth) * 0.25;
    this.netStats.buffered = depth;
    const error = target - this.bufferDepth;
    if (this.netsim?.noClockControl) return;
    this.clockRate = Math.max(MIN_CLOCK_RATE, Math.min(MAX_CLOCK_RATE, 1 + error * 0.02));
    this.netStats.clockRate = this.clockRate;
  }

  /**
   * Server tick the remote view is rendered at (fractional). The underlying
   * clock only moves by rate (up to 2 % of real time, 20 % when far off) so a
   * burst of late packets slews the view instead of jumping it.
   */
  private renderTick(): number {
    const t = this.timing();
    if (!t) return 0;
    const now = performance.now();
    const delayTicks = Math.max(
      MIN_INTERP_DELAY_TICKS,
      Math.min(MAX_INTERP_DELAY_TICKS, Math.ceil((SNAPSHOT_INTERVAL_MS + t.jitterMs) / TICK_MS) + 1),
    );
    this.netStats.interpDelayTicks = delayTicks;
    const target = t.offset + delayTicks * TICK_MS;
    if (this.renderOffset === null) {
      this.renderOffset = target;
    } else {
      const elapsed = Math.max(0, now - this.renderOffsetAt);
      const diff = target - this.renderOffset;
      const maxStep = elapsed * (Math.abs(diff) > 150 ? 0.2 : 0.02);
      this.renderOffset += Math.max(-maxStep, Math.min(maxStep, diff));
    }
    this.renderOffsetAt = now;
    return (now - this.renderOffset) / TICK_MS;
  }

  /** Remote view of the world at the render tick, interpolated by server tick. */
  private interpolated(): GameState | null {
    const n = this.snapshots.length;
    if (n === 0 || this.offsetSamples.length === 0) return null;
    const newest = this.snapshots[n - 1]!;
    const renderTick = this.renderTick();
    if (renderTick >= newest.tick) {
      // Snapshots are late: dead-reckon remote players for a few ticks.
      const ahead = renderTick - newest.tick;
      this.netStats.extrapolated = Math.min(6, Math.floor(ahead));
      return extrapolateState(newest.state, ahead, new Set(this.local.keys()));
    }
    this.netStats.extrapolated = 0;
    if (n === 1) return newest.state;
    const oldest = this.snapshots[0]!;
    if (renderTick <= oldest.tick) return oldest.state;
    for (let i = 0; i < n - 1; i++) {
      const a = this.snapshots[i]!;
      const b = this.snapshots[i + 1]!;
      if (a.tick <= renderTick && renderTick <= b.tick) {
        const t = (renderTick - a.tick) / (b.tick - a.tick);
        return interpolate(a.state, b.state, t);
      }
    }
    return newest.state;
  }

  stats(): NetStats {
    return this.netStats;
  }

  destroy(): void {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
  }
}

function wrap(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

function interpolate(a: GameState, b: GameState, t: number): GameState {
  const byId = new Map(a.players.map((p) => [p.id, p]));
  const players = b.players.map((pb) => {
    const pa = byId.get(pb.id);
    if (!pa || pa.role !== pb.role) return pb;
    return { ...pb, x: lerp(pa.x, pb.x, t), y: lerp(pa.y, pb.y, t), heading: lerpAngle(pa.heading, pb.heading, t) };
  });
  let knife = b.knife;
  if (a.knife.mode === 'flying' && b.knife.mode === 'flying') {
    knife = { ...b.knife, x: lerp(a.knife.x, b.knife.x, t), y: lerp(a.knife.y, b.knife.y, t) };
  }
  return { ...b, players, knife };
}
